import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import InputField from '@shared/components/Atoms/InputField/InputField.component';
import { useTranslations } from '@shared/context/useTranslations';
import { Contact, ContactState } from '@shared/types/UserAccountTypes.interface';
import { cn } from '@shared/helpers/classNames.helper';
import S from './ContactEdit.module.scss';

import { Chip } from '@shared/components/Atoms/Chip/Chip.component';
export interface ContactEditProps {
    contact: Contact,
    useTwoColumns?: boolean,
    allowPrimaryContact?: boolean,
}

export type ContactEditRef = {
    validate: () => ContactState;
    reset: () => void;
};

export const ContactEdit = forwardRef<ContactEditRef, ContactEditProps> (({ contact, useTwoColumns, allowPrimaryContact }: ContactEditProps, ref) => {
    const { getTranslation } = useTranslations();

    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [id] = useState<string | undefined>(contact?.id || undefined);
    const [firstName, setFirstName] = useState<string>(contact?.firstName || "");
    const [lastName, setLastName] = useState<string>(contact?.lastName || "");
    const [phone, setPhone] = useState<string>(contact?.phone || "");
    const [email, setEmail] = useState<string>(contact?.email || "");
    const [isPrimary, setIsPrimary] = useState<boolean>(contact?.isPrimary || false);
  
    const validationErrorsRef = useRef<{ [key: string]: string }>({});

    useEffect(() => {
        setFirstName(contact?.firstName || "");
        setLastName(contact?.lastName || "");
        setPhone(contact?.phone || "");
        setEmail(contact?.email || "");
        setIsPrimary(contact?.isPrimary || false);
    }, [contact]);

    const validateContact = () => {
        const validationErrors: { [key: string]: string } = {};
        
        if (!firstName.trim()) {
            validationErrors.firstName = getTranslation("contacts.errorFirstName");
        }
        if (!phone.trim()) {
            validationErrors.phone = getTranslation("contacts.errorPhone");
        }
        if (!email.trim()) {
            validationErrors.email = getTranslation("contacts.errorEmail");
        } else {
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailRegex.test(email.trim())) {
                validationErrors.email = getTranslation("contacts.errorEmailInvalid");
            }
        }

        validationErrorsRef.current = validationErrors;
        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    }

    // public methods
    useImperativeHandle(ref, () => ({            
        validate: () => {
            const contact = validateContact() ? {
                id,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                phone: phone.trim(),
                isPrimary
            } : null;
          
            return {
                isValid: contact !== null,
                contact,
                saveContact: true,
                errors: validationErrorsRef.current
            }
        },
        reset: () => {
            setFirstName("");
            setLastName("");
            setEmail("");
            setPhone("");
            setIsPrimary(false);
            setErrors({});
            validationErrorsRef.current = {};
        }
    }));
            
    return (
        <div className={cn(S.inputFields, useTwoColumns && S.twoColumns)}>
            <div className={S.span1}>
                <InputField
                    className={cn(S.inputField, S.span3, errors.firstName && S.inputError)}
                    type="text"
                    name="firstName"
                    required
                    value={firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                    label={getTranslation("ContactEdit.firstName")}
                    error={errors.firstName}
                />
            </div>
            <div className={S.span2}>
                <InputField
                    className={cn(S.inputField, S.span3, errors.lastName && S.inputError)}
                    type="text"
                    name="lastName"
                    required
                    value={lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                    label={getTranslation("ContactEdit.lastName")}
                    error={errors.lastName}
                />
            </div>
            <div className={S.span1}>
                <InputField
                    className={cn(S.inputField, S.span3, errors.phone && S.inputError)}
                    type="tel"
                    name="phone"
                    value={phone}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                    label={getTranslation("ContactEdit.phone")}
                    error={errors.phone}
                />
            </div>
            <div className={S.span2}>
                <InputField
                    className={cn(S.inputField, S.span3, errors.email && S.inputError)}
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    label={getTranslation("ContactEdit.email")}
                    error={errors.email}
                />
            </div>
            {allowPrimaryContact && contact.isPrimary ? (
                <div className={S.span1}>
                    <div className={S.chipWrapper}>
                        <Chip className={S.chip} label={getTranslation("contacts.primaryContact")} />
                    </div>
                </div>
            ) : null}

            {allowPrimaryContact && (
                <div className={S.span1}>
                    <label htmlFor="primaryContact" className={S.primaryContactLabel}>
                        <input type="checkbox" name="primaryContact" id="primaryContact" checked={isPrimary} onChange={() => setIsPrimary(!isPrimary)} />
                        {getTranslation("ContactEdit.primaryContactLabel")}
                    </label>
                </div>
            )}
        </div>
    )
});

ContactEdit.displayName = 'ContactEdit';
